/**
 * PlatformAdminPage — @boox.app 内部 DevOps 视图
 *
 * 仅 role === 'platform_admin' 用户可见（通过 Sidebar 入口进入）。
 * 功能：
 * - 列出所有租户 + 作业数 + 最后活动时间
 * - 点击租户 → 查看详细统计（作业、货物、账单、客户数）
 * - 查看该租户最近 20 条作业快照
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Shield,
  Building2,
  RefreshCw,
  Loader2,
  ChevronRight,
  Package,
  FileText,
  Users,
  MessageSquare,
  Clock,
  ArrowLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────

interface TenantRow {
  id: string;
  name: string;
  tier: string;
  jobCount: number;
  lastActivity: string | null;
}

interface TenantStats {
  tenantId: string;
  jobs: number;
  shipments: number;
  ledgerEntries: number;
  customers: number;
  conversations: number;
}

interface JobRow {
  id: string;
  job_number: string;
  job_type: string;
  status: string;
  created_at: string;
}

// ─── Helpers ────────────────────────────────────

function tierBadge(tier: string) {
  const map: Record<string, string> = {
    starter:    'bg-slate-100 text-slate-700',
    growth:     'bg-blue-100 text-blue-700',
    enterprise: 'bg-purple-100 text-purple-700',
  };
  return map[tier?.toLowerCase()] ?? 'bg-muted text-muted-foreground';
}

function fmtDate(iso: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-SG', { month: 'short', day: 'numeric', year: '2-digit' });
}

function statusColor(status: string) {
  const s = status?.toLowerCase();
  if (['completed', 'delivered', 'paid'].includes(s)) return 'text-green-600';
  if (['pending', 'draft'].includes(s)) return 'text-amber-600';
  if (['cancelled', 'failed', 'void'].includes(s)) return 'text-red-500';
  return 'text-blue-600';
}

// ─── Tenant Detail Panel ───────────────────────

function TenantDetail({
  tenant,
  onBack,
}: {
  tenant: TenantRow;
  onBack: () => void;
}) {
  const [stats, setStats] = useState<TenantStats | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get<TenantStats>(`/platform/tenants/${tenant.id}/stats`),
      api.get<JobRow[]>(`/platform/tenants/${tenant.id}/jobs`),
    ])
      .then(([s, j]) => {
        setStats(s);
        setJobs(Array.isArray(j) ? j : []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [tenant.id]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Building2 className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{tenant.name}</h2>
        <Badge className={cn('text-xs capitalize', tierBadge(tenant.tier))}>{tenant.tier}</Badge>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading stats…
        </div>
      ) : (
        <>
          {/* Stats row */}
          {stats && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
              {[
                { label: 'Jobs',          value: stats.jobs,          icon: Package },
                { label: 'Shipments',     value: stats.shipments,     icon: FileText },
                { label: 'Ledger',        value: stats.ledgerEntries, icon: FileText },
                { label: 'Customers',     value: stats.customers,     icon: Users },
                { label: 'Conversations', value: stats.conversations, icon: MessageSquare },
              ].map(({ label, value, icon: Icon }) => (
                <Card key={label} className="py-3">
                  <CardContent className="flex flex-col items-center gap-1 p-0 py-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xl font-bold">{value.toLocaleString()}</span>
                    <span className="text-[11px] text-muted-foreground">{label}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Recent jobs */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Recent 20 Jobs</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {jobs.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">No jobs found</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Job #</th>
                      <th className="px-4 py-2 font-medium">Type</th>
                      <th className="px-4 py-2 font-medium">Status</th>
                      <th className="px-4 py-2 font-medium">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job, i) => (
                      <tr key={job.id} className={cn('border-b last:border-0', i % 2 === 0 ? '' : 'bg-muted/10')}>
                        <td className="px-4 py-2 font-mono text-xs">{job.job_number ?? job.id.slice(0, 8)}</td>
                        <td className="px-4 py-2 uppercase text-xs text-muted-foreground">{job.job_type}</td>
                        <td className={cn('px-4 py-2 capitalize font-medium text-xs', statusColor(job.status))}>{job.status}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{fmtDate(job.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

// ─── Main Page ──────────────────────────────────

export default function PlatformAdminPage() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<TenantRow | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    api.get<TenantRow[]>('/platform/tenants')
      .then(setTenants)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (selected) {
    return (
      <div className="p-6">
        <TenantDetail tenant={selected} onBack={() => setSelected(null)} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100">
            <Shield className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Platform DevOps</h1>
            <p className="text-sm text-muted-foreground">@boox.app internal — cross-tenant visibility</p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading} className="gap-1.5">
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="py-2">
          <CardContent className="flex flex-col p-3 gap-0.5">
            <span className="text-xs text-muted-foreground">Total Tenants</span>
            <span className="text-2xl font-bold">{tenants.length}</span>
          </CardContent>
        </Card>
        <Card className="py-2">
          <CardContent className="flex flex-col p-3 gap-0.5">
            <span className="text-xs text-muted-foreground">Total Jobs</span>
            <span className="text-2xl font-bold">{tenants.reduce((a, t) => a + t.jobCount, 0).toLocaleString()}</span>
          </CardContent>
        </Card>
        <Card className="py-2">
          <CardContent className="flex flex-col p-3 gap-0.5">
            <span className="text-xs text-muted-foreground">Active Last 7d</span>
            <span className="text-2xl font-bold">
              {tenants.filter((t) => {
                if (!t.lastActivity) return false;
                return Date.now() - new Date(t.lastActivity).getTime() < 7 * 86400 * 1000;
              }).length}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Tenant table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            All Tenants
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center gap-2 px-4 py-8 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading tenants…
            </div>
          ) : tenants.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">No tenants found</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Tenant</th>
                  <th className="px-4 py-2 font-medium">Tier</th>
                  <th className="px-4 py-2 font-medium text-right">Jobs</th>
                  <th className="px-4 py-2 font-medium">Last Activity</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t, i) => (
                  <tr
                    key={t.id}
                    className={cn(
                      'border-b last:border-0 cursor-pointer hover:bg-muted/30 transition-colors',
                      i % 2 === 0 ? '' : 'bg-muted/10',
                    )}
                    onClick={() => setSelected(t)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">{t.id.slice(0, 8)}…</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={cn('text-xs capitalize', tierBadge(t.tier))}>{t.tier || '—'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{t.jobCount.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" />
                        {fmtDate(t.lastActivity)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="h-4 w-4 text-muted-foreground ml-auto" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
